import { cx } from '@linaria/core';
import { ChangeEvent, Children, MouseEvent, ReactElement, ReactNode, cloneElement, isValidElement, useId } from 'react';
import { controlDenseStyles, controlDisabledStyles, controlFullWidthLabelStyles, controlGroupRowStyles, controlGroupStyles, controlLabelStartStyles, controlRootStyles, denseControlGroupStyles } from './controlStyles';
import { useCenterRipple } from './ripple';
import "./Radio.linaria.module.css!=!./Radio?./Radio.linaria.module.css?css=H4sIAAAAAAAAA3XPwY6DIBSF4X2fYjZdasBq2tiHISAiKAhFatWm7z5Jx7RzW9ye/Hy5pDQYMqNq0Y7goZO0ten39JNWtg/e6uTmqXO1vzvKueqbJFhXYuym82P3fHdZciMDoOC0TTEbgjVAwy1ixYkD7mPb9nQtAtQOfjaXCmpw29a8aiTkFmHHrCV4GJji8v2S2enOrOe1Tzzl6jqUBdq/bjjxZRoFUdyJJo9MUeemeJAleiHzweQdabogRtj/hRl6X3mt2lkcY62sn1/6H6+wLrJeTzEZH7/kWLzSa/0L1n9X5WQCAAA="
const radioBoxStyles = "atm_y0czlp_1skhajo atm_qz4mht_1skhajo atm_1j0b58d_1skhajo atm_13rymqc_1skhajo atm_zfov2j_1ssbidh atm_18dzxvf_idpfg4 atm_1y3m4k_gktfv atm_ucjyf7_gktfv";
const radioSmallBoxStyles = "atm_1y3m4k_l52nlx atm_ucjyf7_l52nlx";
const RADIO_VIEW_BOX = '2 2 20 20';
const RADIO_RING = 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z';
const RADIO_DOT = 'M8.465 8.465C9.37 7.56 10.62 7 12 7C14.76 7 17 9.24 17 12C17 13.38 16.44 14.63 15.535 15.535C14.63 16.44 13.38 17 12 17C9.24 17 7 14.76 7 12C7 10.62 7.56 9.37 8.465 8.465Z';
type RadioValue = string | number | boolean;
export interface RadioProps {
  checked?: boolean;
  className?: string;
  controlTestId?: string;
  'data-testid'?: string;
  dense?: boolean;
  disabled?: boolean;
  fullWidthLabel?: boolean;
  label?: ReactNode;
  labelStart?: boolean;
  name?: string;
  onChange?(event: ChangeEvent<HTMLInputElement>): void;
  onClick?(event: MouseEvent<HTMLInputElement>): void;
  small?: boolean;
  title?: string;
  value?: RadioValue;
}
const Radio = (props: RadioProps) => {
  const {
    checked = false,
    className,
    controlTestId,
    dense = false,
    disabled = false,
    fullWidthLabel = false,
    label,
    labelStart = false,
    name,
    onChange,
    onClick,
    small = false,
    title,
    value,
    'data-testid': dataTestId
  } = props;
  const {
    rippleRef,
    containerRef,
    onPointerDown
  } = useCenterRipple(disabled);
  return <label className={cx('mfc-radio', controlRootStyles, radioBoxStyles, small && radioSmallBoxStyles, dense && controlDenseStyles, fullWidthLabel && controlFullWidthLabelStyles, labelStart && controlLabelStartStyles, disabled && controlDisabledStyles, className)} data-testid={dataTestId} onPointerDown={onPointerDown} title={title}>
            <span className="control-wrapper" data-testid={controlTestId} ref={containerRef}>
                <span aria-hidden="true" className="control-ripple" ref={rippleRef} />
                <input checked={checked} className="control-input" disabled={disabled} name={name} onChange={onChange} onClick={onClick} readOnly={!onChange} type="radio" value={value === undefined ? undefined : String(value)} />
                <span aria-hidden="true" className="control-box">
                    <svg className="control-icon" viewBox={RADIO_VIEW_BOX}>
                        <path d={RADIO_RING} />
                    </svg>
                    <svg className="control-icon control-icon-checked" viewBox={RADIO_VIEW_BOX}>
                        <path d={RADIO_DOT} />
                    </svg>
                </span>
            </span>
            {label !== undefined && label !== null && <span className="control-text">{label}</span>}
        </label>;
};
export default Radio;
export interface RadioGroupProps {
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
  name?: string;
  onChange?(event: ChangeEvent<HTMLInputElement>): void;
  row?: boolean;
  dense?: boolean;
  value?: RadioValue;
}
const MAX_DEPTH = 4;
export const RadioGroup = (props: RadioGroupProps) => {
  const {
    children,
    className,
    name,
    onChange,
    row = false,
    dense = false,
    value,
    ...rest
  } = props;

  // same name === one native group per document, so scope it per instance
  const groupName = `${name ?? 'radio'}-${useId()}`;
  const withGroupProps = (nodes: ReactNode, depth = 0): ReactNode => Children.map(nodes, child => {
    if (!isValidElement(child)) return child;
    if (child.type === Radio) {
      const option = child as ReactElement<RadioProps>;
      return cloneElement(option, {
        name: option.props.name ?? groupName,
        onChange: option.props.onChange ?? onChange,
        checked: option.props.checked ?? (value !== undefined && String(value) === String(option.props.value))
      });
    }
    const nested = (child.props as {
      children?: ReactNode;
    })?.children;
    if (depth < MAX_DEPTH && nested) {
      return cloneElement(child as ReactElement<{
        children?: ReactNode;
      }>, {
        children: withGroupProps(nested, depth + 1)
      });
    }
    return child;
  });
  return <div className={cx('mfc-radio-group', controlGroupStyles, row && controlGroupRowStyles, dense && denseControlGroupStyles, className)} {...rest}>
            {withGroupProps(children)}
        </div>;
};